import React, { useRef, useEffect, useState } from 'react'
import map from 'lodash/map'
import { useSelector, useDispatch } from 'react-redux'
import ScrollingLoader from '@SiteComponents/ScrollingLoader'
import AccountUserRow from './AccountUserRow'
import Button from '@SiteComponents/Button'
import AccountUsersBulkActionBar from '@SiteContainers/AccountUsersBulkActionBar'
import Plus from '@Images/projects_table_new_project_plus.svg'

import {
  getLoading,
  getAccountUsers,
  getSelectedAccountUserIds,
  getAllAccountUsersSelected,
  getHasLoadedInitially,
} from '@SiteModules/AccountUser/selectors'

import {
  getUserId,
} from '@SiteModules/User/selectors'

import {
  changeSelectedAccountUsers,
  selectAllAccountUsers,
  unselectAccountUsers,
} from '@SiteModules/AccountUser/actions'

import {
  openCreateAccountUserPopup,
} from '@SiteModules/Popup/actions'

import './AccountUsersTable.scss'

const COLUMN_COUNT = 5

interface AccountUsersTableProps {
  setOpenedAccountUserId(openedAccountUserId: number): void
}

const AccountUsersTable: React.FC<AccountUsersTableProps> = props => {
  const dispatch = useDispatch()

  const loading = useSelector(getLoading)
  const accountUsers = useSelector(getAccountUsers)
  const selectedAccountUserIds = useSelector(getSelectedAccountUserIds)
  const userId = useSelector(getUserId)
  const allAccountUsersSelected = useSelector(getAllAccountUsersSelected)
  const hasLoadedInitially = useSelector(getHasLoadedInitially)

  const [hovering, setHovering] = useState(false)
  const checkboxRef = useRef<HTMLLabelElement | null>(null)
  const hasAccountUsersSelected = selectedAccountUserIds.length > 0

  useEffect(() => {
    if (allAccountUsersSelected && hasLoadedInitially) {
      checkboxRef.current?.style?.setProperty('visibility', 'visible')
    } else if (!hovering) {
      checkboxRef.current?.style?.setProperty('visibility', 'hidden')
    }
  }, [allAccountUsersSelected])

  const onMouseEnter = () => {
    setHovering(true)
    checkboxRef.current?.style?.setProperty('visibility', 'visible')
  }

  const onMouseLeave = () => {
    setHovering(false)
    if (allAccountUsersSelected) { return }

    checkboxRef.current?.style?.setProperty('visibility', 'hidden')
  }

  const handleChangeSelectedAccountUsers = (e: React.ChangeEvent<HTMLInputElement>, accountUserId: number) => {
    dispatch(changeSelectedAccountUsers({ accountUserId, selectAccountUser: e.target.checked }))
  }

  const handleSelectAllAccountUsers = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      dispatch(selectAllAccountUsers())
    } else {
      dispatch(unselectAccountUsers())
    }
  }

  const handleOpenCreateAccountUserPopup = () => {
    dispatch(openCreateAccountUserPopup())
  }

  const renderNewUserButton = () => {
    return (
      <div className="AccountUsersTable__button">
        <Button
          caption="New user"
          icon={<Plus />}
          styleType="brand"
          size="large"
          onClick={handleOpenCreateAccountUserPopup}
        />
      </div>
    )
  }

  const renderTable = () => {
    return (
      <table className="AccountUsersTable__table">
        <thead>
          {renderTableHeader()}
        </thead>
        <tbody className="AccountUsersTable__body">
          {tableRows}
          {loading && renderLoader()}
        </tbody>
      </table>
    )
  }

  const renderTableHeader = () => {
    return (
      <tr
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <th className="AccountUsersTable__check">
          {renderCheckbox()}
        </th>
        <th className="AccountUsersTable__user">
          User
        </th>
        <th className="AccountUsersTable__email">
          Email adress
        </th>
        <th className="AccountUsersTable__joined-on">
          Joined on
        </th>
        <th className="AccountUsersTable__role">
          Account role
        </th>
      </tr>
    )
  }

  const tableRows = map(accountUsers, (accountUser, index) => {
    const selected = selectedAccountUserIds.includes(accountUser.id)

    return (
      <AccountUserRow
        key={index}
        index={index}
        accountUser={accountUser}
        changeSelectedAccountUsers={handleChangeSelectedAccountUsers}
        selected={selected}
        setOpenedAccountUserId={props.setOpenedAccountUserId}
        isOwnUser={accountUser.attributes.userId === userId}
      />
    )
  })

  const renderCheckbox = () => {
    return (
      <label className="AccountUsersTable__checkbox" ref={checkboxRef}>
        <input
          type="checkbox"
          onChange={handleSelectAllAccountUsers}
          checked={allAccountUsersSelected}
        />
        <div className="AccountUsersTable__checkmark" />
      </label>
    )
  }

  const renderLoader = () => {
    return (
      <tr>
        <td colSpan={COLUMN_COUNT}>
          <ScrollingLoader scale={0.5}/>
        </td>
      </tr>
    )
  }

  return (
    <div className="AccountUsersTable">
      {renderNewUserButton()}
      {renderTable()}
      {hasAccountUsersSelected && (
        <AccountUsersBulkActionBar />
      )}
    </div>
  )
}

export default AccountUsersTable
